import { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import { Modal, Form, Checkbox, Radio, Tooltip } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import {
  Arrow,
  FormCard,
  FormItem,
  Heading,
  Input,
  TextArea
} from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { showToast } from 'common/utils'
import StockCount from '../iconsV2/StockCount'
import { Button, Text } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import Asterix from 'screens/Rostering/RotaPattern/components/Asterix'
import StockBanner from './StockBanner'
import AdditionalDetailsSection from './AdditionalDetailsSection'
import { useStockCheck } from '../hooks/useStockCheck'
import { StockRecord, DiscrepancyStatus, StockCheckData } from '../types'
import { convertMediaAttachmentsToFileList } from '../utils/stockRecordUtils'

type StockCheckModalProps = {
  open: boolean
  medicationName: string
  expectedStock: number
  residentId: string
  residentMedicationId: string
  unit?: string
  onCancel: () => void
  onSuccess?: () => void
  editMode?: boolean
  record?: StockRecord
}

const StockCheckModal = ({
  open,
  medicationName,
  expectedStock,
  residentId,
  residentMedicationId,
  unit,
  onCancel,
  onSuccess,
  editMode = false,
  record
}: StockCheckModalProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const [form] = Form.useForm()
  const displayUnit = record?.unit || unit || ''

  const getInitialData = (record: StockRecord | undefined) => {
    if (!record) return undefined
    const description = Array.isArray(record.notes)
      ? record.notes.join(', ')
      : record.notes || ''

    const stockCount = record.stockCount ?? record.resultingStockLevel ?? 0

    let calculatedExpectedStock =
      record.expectedStock ?? record.previousStockLevel
    if (
      calculatedExpectedStock === undefined &&
      stockCount !== undefined &&
      record.quantityChange !== undefined
    ) {
      calculatedExpectedStock = stockCount - record.quantityChange
    }
    calculatedExpectedStock = calculatedExpectedStock ?? 0

    const isDiscrepancyResolved =
      record.discrepancy?.status === DiscrepancyStatus.RESOLVED
    const hasUnresolvedDiscrepancy =
      record.hasDiscrepancy ||
      (record.discrepancy &&
        record.discrepancy.status !== DiscrepancyStatus.RESOLVED)

    return {
      recordId: record.id,
      description,
      stockCount: record.stockCount,
      adjustment: stockCount - calculatedExpectedStock,
      additionalDetails: false,
      isExpectedStockAccurate: stockCount === calculatedExpectedStock,
      resolveDiscrepancy: isDiscrepancyResolved
        ? 'yes'
        : hasUnresolvedDiscrepancy
          ? 'no'
          : null,
      discrepancyReason: record.discrepancy?.reason || ''
    }
  }

  const initialData = getInitialData(record)

  let actualExpectedStock: number = expectedStock
  if (editMode && record) {
    const stockCount = record.stockCount ?? record.resultingStockLevel ?? 0
    let calculatedExpectedStock =
      record.expectedStock ?? record.previousStockLevel
    if (
      calculatedExpectedStock === undefined &&
      stockCount !== undefined &&
      record.quantityChange !== undefined
    ) {
      calculatedExpectedStock = stockCount - record.quantityChange
    }
    actualExpectedStock = calculatedExpectedStock ?? 0
  }

  const initialValues = {
    description: initialData?.description || '',
    resolveDiscrepancy: initialData?.resolveDiscrepancy || null,
    discrepancyReason: initialData?.discrepancyReason || ''
  }

  const [stockCount, setStockCount] = useState<number | null>(
    editMode && initialData?.stockCount !== undefined
      ? initialData.stockCount
      : null
  )

  const hasMedia = editMode && record?.mediaAttachments?.length

  const [fileList, setFileList] = useState<any[]>(() => {
    if (!hasMedia) return []
    return convertMediaAttachmentsToFileList(
      record!.mediaAttachments!,
      record!.id
    )
  })

  const [showAdditionalDetails, setShowAdditionalDetails] = useState(!!hasMedia)
  const [isExpectedStockAccurate, setIsExpectedStockAccurate] = useState(
    editMode && initialData?.stockCount !== undefined
      ? initialData.stockCount === actualExpectedStock
      : false
  )
  const [showAdjustmentMode, setShowAdjustmentMode] = useState(false)
  const [adjustmentInput, setAdjustmentInput] = useState<number | null>(null)
  const [adjustmentError, setAdjustmentError] = useState<string | null>(null)

  // Track initial values for dirty detection
  const initialFileListRef = useRef<any[]>([])
  const initialShowAdditionalDetailsRef = useRef<boolean>(false)
  const initialStockCountRef = useRef<number | null>(null)
  const initialIsExpectedStockAccurateRef = useRef<boolean>(false)
  const initialShowAdjustmentModeRef = useRef<boolean>(false)
  const prevOpenRef = useRef<boolean>(false)

  useEffect(() => {
    // Only capture initial values when modal transitions from closed to open
    if (open && !prevOpenRef.current) {
      // Deep copy fileList to avoid reference issues
      initialFileListRef.current = fileList.map((f) => ({ ...f }))
      initialShowAdditionalDetailsRef.current = showAdditionalDetails
      initialStockCountRef.current = stockCount
      initialIsExpectedStockAccurateRef.current = isExpectedStockAccurate
      initialShowAdjustmentModeRef.current = showAdjustmentMode
    }
    prevOpenRef.current = open
  }, [open])

  const handleFileChange = (info: any) => {
    setFileList(info.fileList)
  }

  const resetInputs = () => {
    setStockCount(null)
    setAdjustmentInput(null)
    setAdjustmentError(null)
  }

  const { createStockCheck, updateStockCheck, isLoading } = useStockCheck({
    residentId,
    residentMedicationId,
    expectedStock: actualExpectedStock ?? 0,
    onSuccess: () => {
      showToast({
        type: 'success',
        title: editMode
          ? t('STOCK_CHECK_UPDATED_SUCCESS')
          : t('STOCK_CHECK_CREATED_SUCCESS')
      })
      onSuccess?.()
    },
    onError: () => {
      showToast({
        type: 'error',
        title: t('STOCK_CHECK_ERROR')
      })
    }
  })

  const handleEnterAdjustment = () => {
    resetInputs()
    setShowAdjustmentMode(true)
  }

  const handleSetStockCount = () => {
    resetInputs()
    setShowAdjustmentMode(false)
  }

  const handleStockCountChange = (value: string) => {
    if (!value || value === '') {
      setStockCount(null)
      return
    }
    const numVal = Number(value)
    if (!isNaN(numVal)) {
      setStockCount(numVal)
    }
  }

  const handleAdjustmentInputChange = (value: string) => {
    if (!value || value === '') {
      resetInputs()
      return
    }

    const numVal = Number(value)
    if (isNaN(numVal)) return

    setAdjustmentInput(numVal)

    if (numVal < -(actualExpectedStock ?? 0)) {
      setAdjustmentError(t('ADJUSTMENT_TOO_HIGH'))
      setStockCount(null)
    } else {
      setAdjustmentError(null)
      setStockCount((actualExpectedStock ?? 0) + numVal)
    }
  }

  const handleSave = () => {
    form.validateFields().then((values) => {
      const finalStockCount = isExpectedStockAccurate
        ? (actualExpectedStock ?? 0)
        : (stockCount ?? 0)
      const adjustment = finalStockCount - (actualExpectedStock ?? 0)
      const mediaFiles = fileList
        .map((file: any) => file.originFileObj)
        .filter(Boolean) as File[]

      const currentFileKeys = new Set(
        fileList
          .filter((file: any) => !file.originFileObj && file.key)
          .map((file: any) => file.key)
      )
      const removedKeys =
        editMode && record?.mediaAttachments
          ? record.mediaAttachments
              .filter((attachment) => !currentFileKeys.has(attachment.key))
              .map((attachment) => attachment.key)
          : []

      const data: StockCheckData = {
        description: values.description || '',
        stockCount: finalStockCount,
        adjustment,
        additionalDetails: showAdditionalDetails,
        isExpectedStockAccurate,
        resolveDiscrepancy: values.resolveDiscrepancy || null,
        discrepancyReason: values.discrepancyReason || '',
        media: mediaFiles,
        mediaAttachmentKeysToRemove:
          removedKeys.length > 0 ? removedKeys : undefined
      }

      if (editMode && record?.id && record?.action) {
        updateStockCheck({
          recordId: record.id,
          data,
          actionType: record.action
        })
      } else {
        createStockCheck(data)
      }
    })
  }

  const currentStockCount = isExpectedStockAccurate
    ? actualExpectedStock
    : stockCount
  const discrepancy =
    currentStockCount !== null
      ? (currentStockCount ?? 0) - (actualExpectedStock ?? 0)
      : null

  // In edit mode, check if record has an unresolved discrepancy
  const recordHasUnresolvedDiscrepancy =
    editMode &&
    record &&
    (record.hasDiscrepancy ||
      (record.discrepancy &&
        record.discrepancy.status !== DiscrepancyStatus.RESOLVED))

  const isDiscrepancyResolved =
    editMode &&
    record &&
    record.discrepancy?.status === DiscrepancyStatus.RESOLVED

  const hasDiscrepancy =
    recordHasUnresolvedDiscrepancy ||
    (discrepancy !== null && discrepancy !== 0)
  const hasError = adjustmentError !== null

  // Check if form is dirty
  const isFormDirty = () => {
    // Check if form fields have been touched
    const isFieldsTouched = form.isFieldsTouched()

    // Check if state values have changed
    const stockCountChanged = stockCount !== initialStockCountRef.current
    const additionalDetailsChanged =
      showAdditionalDetails !== initialShowAdditionalDetailsRef.current
    const isExpectedStockAccurateChanged =
      isExpectedStockAccurate !== initialIsExpectedStockAccurateRef.current
    const showAdjustmentModeChanged =
      showAdjustmentMode !== initialShowAdjustmentModeRef.current

    // Check if file list has changed (compare by length and file keys/names)
    const fileListChanged = (() => {
      if (fileList.length !== initialFileListRef.current.length) return true
      const currentKeys = fileList
        .map((f) => f.uid || f.name || f.key)
        .filter(Boolean)
      const initialKeys = initialFileListRef.current
        .map((f) => f.uid || f.name || f.key)
        .filter(Boolean)
      if (currentKeys.length !== initialKeys.length) return true
      return currentKeys.some((key, idx) => key !== initialKeys[idx])
    })()

    // Check if form values differ from initial values
    const formValues = form.getFieldsValue()
    const valuesChanged = Object.keys(formValues).some((key) => {
      const currentValue = formValues[key]
      const initialValue = initialValues[key as keyof typeof initialValues]
      return currentValue !== initialValue
    })

    return (
      isFieldsTouched ||
      stockCountChanged ||
      fileListChanged ||
      additionalDetailsChanged ||
      isExpectedStockAccurateChanged ||
      showAdjustmentModeChanged ||
      valuesChanged
    )
  }

  const handleCancel = () => {
    if (isFormDirty()) {
      Modal.confirm({
        title: t('MODAL_TITLE', { ns: NAMESPACE.GENERAL }),
        content: t('MODAL_CONTENT', { ns: NAMESPACE.GENERAL }),
        okText: t('EXIT_WITHOUT_SAVING', { ns: NAMESPACE.GENERAL }),
        cancelText: t('CANCEL'),
        onOk: () => {
          form.resetFields()
          onCancel()
        }
      })
    } else {
      onCancel()
    }
  }

  const getDiscrepancyDisplay = () => {
    if (showAdjustmentMode) {
      // In adjustment mode, show new stock value
      if (isExpectedStockAccurate) {
        return actualExpectedStock ?? ''
      }
      return hasError || adjustmentInput === null ? '' : stockCount
    }
    // In stock count mode, show discrepancy
    if (isExpectedStockAccurate) return '0'
    return stockCount === null
      ? ''
      : `${discrepancy! > 0 ? '+' : ''}${discrepancy!}`
  }

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={648}
      title={
        <TitleContainer>
          <StockCount />
          <ModalTitle>
            <Heading level={3}>
              {editMode ? t('EDIT_STOCK_CHECK') : t('STOCK_CHECK')}
            </Heading>
          </ModalTitle>
        </TitleContainer>
      }
    >
      <Divider />

      <Form form={form} initialValues={initialValues} preserve={false}>
        <ModalContentContainer>
          <FormCard level={2}>
            <FormItem
              name="description"
              label={<Text>{t('DESCRIPTION')}</Text>}
              full
              rules={[{ max: 150, message: t('DESCRIPTION_MAX_LENGTH') }]}
            >
              <StyledTextArea
                rows={4}
                placeholder={t('ENTER_DESCRIPTION')}
                maxLength={150}
                showCount
                data-testid="stock-check-description"
              />
            </FormItem>
          </FormCard>

          <FormCard
            level={2}
            title={
              <CardTitleContainer>
                <MedicationNameHeading weight="bold" level={5}>
                  {medicationName}
                </MedicationNameHeading>
                {!editMode && (
                  <Button
                    variant="text"
                    style={{ whiteSpace: 'nowrap' }}
                    onClick={
                      showAdjustmentMode
                        ? handleSetStockCount
                        : handleEnterAdjustment
                    }
                  >
                    {showAdjustmentMode
                      ? t('SET_STOCK_COUNT')
                      : t('ENTER_STOCK_ADJUSTMENT')}
                  </Button>
                )}
              </CardTitleContainer>
            }
          >
            <AdjustmentContainer>
              <CheckboxContainer>
                <Checkbox
                  checked={isExpectedStockAccurate}
                  onChange={(e) => setIsExpectedStockAccurate(e.target.checked)}
                  disabled={editMode}
                >
                  <Text>{t('EXPECTED_STOCK_ACCURATE')}</Text>
                </Checkbox>
              </CheckboxContainer>
              <CalculationContainer>
                <CalculationColumn $align="center" $width="140px">
                  <Label>{t('EXPECTED_STOCK')}</Label>
                  <Value weight="bold">
                    {actualExpectedStock
                      ? `${actualExpectedStock} ${displayUnit}`
                      : '-'}
                  </Value>
                </CalculationColumn>
                {showAdjustmentMode ? (
                  <PlusIconWrapper>
                    <PlusOutlined />
                  </PlusIconWrapper>
                ) : (
                  <IconWrapper>
                    <Arrow direction="right" size={18} />
                  </IconWrapper>
                )}
                <CalculationColumn $flex>
                  <LabelContainer>
                    <Asterix />
                    <Label>
                      {showAdjustmentMode ? t('ADJUSTMENT') : t('STOCK_COUNT')}
                    </Label>
                  </LabelContainer>
                  <Input
                    type="number"
                    step="1"
                    value={
                      showAdjustmentMode
                        ? (adjustmentInput ?? '')
                        : (stockCount ?? '')
                    }
                    onChange={(e) => {
                      showAdjustmentMode
                        ? handleAdjustmentInputChange(e.target.value)
                        : handleStockCountChange(e.target.value)
                    }}
                    disabled={editMode || isExpectedStockAccurate}
                    status={hasError ? 'error' : undefined}
                    style={{ width: '100%' }}
                    data-testid="stock-count-input"
                    suffix={displayUnit}
                  />
                </CalculationColumn>
                <IconWrapper>
                  <Arrow direction="right" size={18} />
                </IconWrapper>
                <CalculationColumn $align="center" $width="140px">
                  <Label>
                    {showAdjustmentMode ? t('NEW_STOCK') : t('DISCREPANCY')}
                  </Label>
                  {showAdjustmentMode ? (
                    <DiscrepanciesContainer>
                      <DiscrepancyValue
                        $isPositive={discrepancy !== null && discrepancy >= 0}
                        $noDiscrepancy={
                          discrepancy !== null && discrepancy === 0
                        }
                        weight="bold"
                      >
                        {(() => {
                          const display = getDiscrepancyDisplay()
                          return display !== ''
                            ? `${display} ${displayUnit}`
                            : ''
                        })()}
                      </DiscrepancyValue>
                      {hasDiscrepancy &&
                        discrepancy !== null &&
                        discrepancy !== 0 && (
                          <DiscrepancyValueUnderNewStock level={2}>
                            {discrepancy > 0 ? '+' : ''}
                            {discrepancy} {t('DISCREPANCY')}
                          </DiscrepancyValueUnderNewStock>
                        )}
                    </DiscrepanciesContainer>
                  ) : (
                    <DiscrepancyValue
                      $isPositive={discrepancy !== null && discrepancy >= 0}
                      $noDiscrepancy={discrepancy !== null && discrepancy === 0}
                      weight="bold"
                    >
                      {(() => {
                        const display = getDiscrepancyDisplay()
                        return display !== '' ? `${display} ${displayUnit}` : ''
                      })()}
                    </DiscrepancyValue>
                  )}
                </CalculationColumn>
              </CalculationContainer>
              {hasError && !editMode && (
                <StockBanner type="error">{adjustmentError}</StockBanner>
              )}
              {!hasError && hasDiscrepancy && !editMode && (
                <StockBanner type="warning">
                  {t('DISCREPANCY_DETECTED')}
                </StockBanner>
              )}
              {!hasError && hasDiscrepancy && (
                <>
                  <FormItem
                    name="resolveDiscrepancy"
                    label={<Text>{t('RESOLVE_DISCREPANCY')}?</Text>}
                    required
                    rules={[
                      {
                        required: true,
                        message: t('RESOLVE_DISCREPANCY_REQUIRED')
                      }
                    ]}
                  >
                    <StyledRadioGroup disabled={isDiscrepancyResolved}>
                      <Radio value="no">
                        {<RadioLabel>{t('NO_LEAVE_OPEN')}</RadioLabel>}
                      </Radio>
                      <Radio value="yes">
                        {<RadioLabel>{t('YES_MARKED_RESOLVED')}</RadioLabel>}
                      </Radio>
                    </StyledRadioGroup>
                  </FormItem>

                  <FormItem
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                      prevValues.resolveDiscrepancy !==
                      currentValues.resolveDiscrepancy
                    }
                  >
                    {({ getFieldValue }) =>
                      getFieldValue('resolveDiscrepancy') === 'yes' ? (
                        <FormItem
                          name="discrepancyReason"
                          label={<Text>{t('REASON_FOR_DISCREPANCY')}</Text>}
                          required
                          full
                          rules={[
                            {
                              required: true,
                              message: t('REASON_FOR_DISCREPANCY_REQUIRED')
                            }
                          ]}
                        >
                          <TextArea
                            rows={4}
                            placeholder={t('ENTER_REASON')}
                            maxLength={150}
                            showCount
                          />
                        </FormItem>
                      ) : null
                    }
                  </FormItem>
                </>
              )}
            </AdjustmentContainer>
          </FormCard>

          <AdditionalDetailsSection
            showAdditionalDetails={showAdditionalDetails}
            onToggle={setShowAdditionalDetails}
            fileList={fileList}
            onFileChange={handleFileChange}
          />
        </ModalContentContainer>
      </Form>

      <Divider />

      <ButtonsContainer>
        <Button onClick={handleCancel} data-testid="cancel-btn">
          {t('CANCEL')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={hasError || isLoading}
          isLoading={isLoading}
          data-testid="save-btn"
        >
          {t('SAVE')}
        </Button>
      </ButtonsContainer>
    </Modal>
  )
}

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 20px;
    height: 20px;
  }
`

const DiscrepanciesContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
`
const ModalTitle = styled.div`
  text-transform: none;
  font-weight: normal;
`

const ModalContentContainer = styled.div`
  margin: 24px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const MedicationNameHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const RadioLabel = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const CardTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const CalculationContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`

const CalculationColumn = styled.div<{
  $align?: 'left' | 'center'
  $width?: string
  $flex?: boolean
}>`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  padding: 0 8px;
  gap: 8px;
  align-items: ${({ $align = 'left' }) => $align};
  ${({ $width }) => $width && `width: ${$width};`}
  ${({ $flex }) => $flex && `flex: 1;`}
`

const IconWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-shrink: 0;
  padding: 0 8px;
  padding-top: 24px;
`

const PlusIconWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-shrink: 0;
  padding: 0 8px;
  padding-top: 24px;

  .anticon {
    font-size: 18px;
    color: ${({ theme }) => theme.color.neutral.color1};
  }
`

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const Label = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
  min-height: 24px;
`

const Value = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color1};
  align-self: center;
  min-height: 32px;
  display: flex;
  align-items: center;
`

const DiscrepancyValue = styled(Text)<{
  $isPositive: boolean
  $noDiscrepancy?: boolean
}>`
  color: ${({ theme }) => theme.color.neutral.color1};
  align-self: center;
  min-height: 32px;
  display: flex;
  align-items: center;
`

const DiscrepancyValueUnderNewStock = styled(Text)`
  color: ${({ theme }) => theme.color.status.warning};
  align-self: center;
  display: flex;
  align-items: center;
`

const StyledTextArea = styled(TextArea)`
  width: 100%;
  margin-bottom: 16px;
`

const CheckboxContainer = styled.div`
  width: 100%;
`

const AdjustmentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StyledRadioGroup = styled(Radio.Group)`
  .ant-radio-wrapper {
    background-color: transparent !important;
    margin: 0;
    padding: 4px;
  }
`

const ButtonsContainer = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`

export default StockCheckModal
